import React, { useContext, useEffect } from 'react';
import Input from '../../../elements/Input';
import { isNilOrEmpty, removeInvalidCharacters, replaceAll } from '../../../../helpers/utils';
import { Context } from '@front10/helpers/dist/Context';
import { getUuid } from '@front10/helpers/dist/uuid';
import { CSSProperties } from '@front10';
import Text from '../../../elements/Text';
import Box from '../../../elements/Box';
import Button from '../../../elements/Button';
import Icon from '../../../elements/Icon';
import { FnOnDayClick, NavigationValidationFunctions, useHandleCalendarNavigation } from './hooks';
import { searchSeparator } from '../../../../helpers/date';
import VisuallyHidden from '@reach/visually-hidden';
import { StringDateRange } from '../types';
import {
  addMonthsNumber,
  getDayData,
  getFirstDayOfMonth,
  getModifiers,
  isDayInRange,
  lastDayOfMonth
} from './helper';
const DATE_OUT_OF_RANGE = 0;
const INCORRECT_FORMAT = 1;
const VALID_DATE = 2;

const componentIdLeft = getUuid();
const componentIdRight = getUuid();

const appendValue = (source: string, position: number, value: string) => {
  return source.indexOf(value, position) - 1 === position ? value : '';
};

type GenericDateRangeEvent = (event: $TSFixMe, component: 'start' | 'end') => void;
export type InputDateRangePickerProps = {
  allowedCharacters: string;
  component?: 'start' | 'end';
  className?: string;
  componentId?: string;
  style?: CSSProperties;
  clearButton: any;
  rightInputRef: any;
  leftInputRef: any;
  leftComponentReference?: any;
  rightComponentReference?: any;
  clearAction: Function;
  format: string;
  inputItem: any;
  leftInput: any;
  rightInput: any;
  iconClose: string;
  invalidClass: string;
  labelKey: string;
  departureLabelKey: string;
  returnLabelKey: string;
  onValueClass: string;
  focusClass?: string;
  selectedInputBorder?: string;
  onFocus?: GenericDateRangeEvent;
  onChange?: GenericDateRangeEvent;
  onClick?: GenericDateRangeEvent;
  onKeyDown?: GenericDateRangeEvent;
  openPopupOnKeyDown?: boolean;
  openPopupOnClick?: boolean;
  onBlur?: (event: $TSFixMe, component: 'start' | 'end') => void;
  textLabel: Record<string, object>;
  rightIcon: any;
  rightIconOpen: any;
  errorLabelComponent?: any;
  departInputPlaceholderLabelKey?: string;
  returnInputPlaceholderLabelKey?: string;
  showSeparator: boolean;
  showLabel: boolean;
  isValid: boolean;
  isValidEnd: boolean;
  isPopopverOpen: boolean;
  isMobileOpen?: boolean;
  value?: StringDateRange;
  onDayClick: FnOnDayClick;
  dispatch: Function;
  state: any;
  modifiers: any;
  closePopoverAfterAction?: boolean;
  isMobile?: boolean;
  isRequired?: boolean;
  isClearEmptyInput?: boolean;
  showCloseDepartureIcon?: boolean;
  showCloseReturnIcon?: boolean;
  buttonDepartureIconId?: string;
  buttonDepartureClearId?: string;
  buttonReturnIconId?: string;
  buttonReturnClearId?: string;
  childrenLeft?: any;
  childrenRight?: any;
};

const InputDateRangePicker = (props: InputDateRangePickerProps) => {
  const {
    allowedCharacters,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    component = 'start',
    clearButton,
    clearAction,
    className = '',
    // componentId,
    style = {},
    rightComponentReference,
    leftComponentReference,
    format = 'DD MMM YYYY',
    leftInputRef = null,
    rightInputRef = null,
    iconClose,
    invalidClass,
    focusClass = '',
    departInputPlaceholderLabelKey = 'cmp-label--datepicker-depart',
    returnInputPlaceholderLabelKey = 'cmp-label--datepicker-return',
    inputItem = {},
    leftInput = {},
    rightInput = {},
    // labelKey = 'cmp-date-range-selector-label',
    departureLabelKey = 'cmp-date-range-departure-label',
    returnLabelKey = 'cmp-date-range-return-label',
    onValueClass,
    onChange = () => {},
    onClick = () => {},
    onFocus = () => {},
    onBlur = () => {},
    onDayClick = () => {},
    openPopupOnClick = true,
    openPopupOnKeyDown = true,
    dispatch,
    state,
    rightIcon: { icon: rightIcon, ...rightIconSettings },
    rightIconOpen,
    showLabel,
    textLabel,
    showCloseDepartureIcon,
    showCloseReturnIcon,
    // showSeparator = true,
    value,
    isValid = true,
    isValidEnd = true,
    isPopopverOpen = false,
    isMobileOpen = false,
    modifiers,
    closePopoverAfterAction = true,
    isMobile = false,
    isRequired = false,
    errorLabelComponent = {},
    isClearEmptyInput = false,
    selectedInputBorder = '',
    buttonDepartureIconId,
    buttonDepartureClearId,
    buttonReturnIconId,
    buttonReturnClearId,
    childrenLeft,
    childrenRight
  } = props;
  const context = useContext(Context);
  const { translate } = context;

  //validations functions
  const validations: NavigationValidationFunctions = {
    validateDay: (date: Date) => {
      const dateModifiers = getModifiers(date, modifiers);
      return (
        !dateModifiers.disabled &&
        dateModifiers.interactive &&
        !dateModifiers.blackout &&
        !dateModifiers.hidden
      );
    },
    dataFromDay: (date: Date) => {
      return getDayData(date, state.months);
    },
    isDateOutOfView: (date: Date) => {
      //show data when are off screen
      const endDisplayRange = lastDayOfMonth(
        addMonthsNumber(state.displayDate, state.numberOfMonths - 1)
      );
      const startDisplayRange = getFirstDayOfMonth(state.displayDate);
      return !isDayInRange(date, { start: startDisplayRange, end: endDisplayRange });
    }
  };

  const navigationHandler = useHandleCalendarNavigation({
    initialDate: state.preSelected,
    format,
    dispatch,
    onDayClick,
    validations,
    isPopopverOpen,
    inputRef: state.selectedComponent === 'start' ? leftInputRef : rightInputRef,
    component: state.selectedComponent,
    preSelected: state.preSelected,
    availableDays: state.availableDays
  });

  const classNameCmpLeft = `fr-dates-range-selector ${
    !isValid && state.inputValue?.start !== '' ? `${invalidClass || 'fr-input-invalid'}` : ''
  }`;
  const classNameCmpRight = `fr-dates-range-selector ${
    !isValidEnd && state.inputValue?.end !== '' ? `${invalidClass || 'fr-input-invalid'}` : ''
  }`;

  let currentOnValueClass: string | undefined = '';
  if (!isNilOrEmpty(onValueClass) && !isNilOrEmpty(value)) {
    currentOnValueClass = onValueClass;
  }
  const separator = searchSeparator(format);

  const handleOnChange: GenericDateRangeEvent = (event, component) => {
    if (isClearEmptyInput && event.target.value.length === 0) {
      dispatch({ type: 'CLEAR_ACTION' });
    }
    onChange(event, component);
  };

  const handleRightButtonAction = (props: any) => {
    const { event } = props;
    if (!isPopopverOpen) {
      dispatch({ type: 'OPEN_POPOVER_ACTION' });
    } else {
      if (closePopoverAfterAction) {
        dispatch({ type: 'CLOSE_POPOVER_ACTION' });
      }
    }
    onClick(event, state.selectedComponent);
  };
  const handleOnFocus: GenericDateRangeEvent = (event, component) => {
    if (component === 'start' && isValid && !isMobile) {
      if (leftComponentReference && leftComponentReference.current) {
        leftComponentReference?.current?.classList.add(''.concat(focusClass || 'fr-focus:focus'));
      }
    } else {
      !isMobile &&
        leftComponentReference?.current?.classList.remove(focusClass || 'fr-focus:focus');
    }

    if (component === 'end' && isValid && !isMobile) {
      if (rightComponentReference && rightComponentReference.current) {
        rightComponentReference?.current?.classList.add(''.concat(focusClass || 'fr-focus:focus'));
      }
    } else {
      !isMobile &&
        rightComponentReference?.current?.classList.remove(focusClass || 'fr-focus:focus');
    }
    onFocus(event, component);
  };

  // React.useEffect(() => {
  //   if (classNameCmpLeft.includes(invalidClass)) handleOnFocus(null, 'start');
  //   else leftInputRef.current.focus();
  // }, [classNameCmpLeft]);

  const handleKeyDown: GenericDateRangeEvent = (event: $TSFixMe, component) => {
    event.persist();
    const { value } = event.target;

    const regex = new RegExp(allowedCharacters);
    if (!regex.test(event.key)) {
      event.preventDefault();
    }
    if (event.key === 'ArrowDown' && openPopupOnKeyDown) {
      dispatch({ type: 'SET_DAY_FOCUS_BY_KEYBOARD', payload: true });
      dispatch({ type: 'OPEN_POPOVER_ACTION' });
    }

    if (event.key !== 'Backspace') {
      if (value) {
        event.target.value += appendValue(format, value.length - 1, separator);
      }
    }
    if (event.target.value === '' && event.key === 'Backspace') {
      dispatch({ type: 'CLEAR_ACTION' });
      dispatch({
        type: 'SET_VALID_END',
        payload: true
      });
    }
    // dispatch({ type: 'SET_INPUT_VALUE', payload: event.target.value, component });

    // if (component === 'start') {
    navigationHandler(event);
    // } else {
    //   rightNavigationHandler(event);
    // }
  };

  const handleOnBlur: GenericDateRangeEvent = (event, component) => {
    onBlur(event, component);
  };
  useEffect(() => {
    if (
      leftComponentReference &&
      leftComponentReference?.current &&
      state.selectedComponent === 'start' &&
      !isPopopverOpen &&
      leftComponentReference?.current?.classList?.contains(
        selectedInputBorder || 'fr-selected-input'
      )
    ) {
      leftComponentReference?.current?.classList?.remove(
        selectedInputBorder || 'fr-selected-input'
      );
    }
    if (
      rightComponentReference &&
      rightComponentReference?.current &&
      state.selectedComponent === 'end' &&
      !isPopopverOpen &&
      rightComponentReference?.current?.classList?.contains(
        selectedInputBorder || 'fr-selected-input'
      )
    ) {
      rightComponentReference?.current?.classList?.remove(
        selectedInputBorder || 'fr-selected-input'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPopopverOpen, state.selectedComponent]);

  useEffect(() => {
    if (state?.selectedRange?.start !== undefined) {
      if (
        leftComponentReference &&
        leftComponentReference?.current &&
        state.selectedComponent === 'end' &&
        isPopopverOpen &&
        leftComponentReference?.current?.classList?.contains(
          selectedInputBorder || 'fr-selected-input'
        )
      ) {
        leftComponentReference?.current?.classList?.remove(
          selectedInputBorder || 'fr-selected-input'
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.selectedRange?.start]);

  useEffect(() => {
    if (state?.selectedRange?.end !== undefined) {
      if (
        rightComponentReference &&
        rightComponentReference?.current &&
        state.selectedComponent === 'start' &&
        isPopopverOpen &&
        rightComponentReference?.current?.classList?.contains(
          selectedInputBorder || 'fr-selected-input'
        )
      ) {
        rightComponentReference?.current?.classList?.remove(
          selectedInputBorder || 'fr-selected-input'
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.selectedRange?.end]);

  useEffect(() => {
    if (
      state.valid &&
      leftComponentReference &&
      leftComponentReference?.current &&
      state.selectedComponent === 'start' &&
      isPopopverOpen &&
      !leftComponentReference?.current?.classList?.contains(
        selectedInputBorder || 'fr-selected-input'
      )
    ) {
      leftComponentReference?.current?.classList.add(
        ''.concat(selectedInputBorder || 'fr-selected-input')
      );
    }
    if (
      isValidEnd &&
      rightComponentReference &&
      rightComponentReference?.current &&
      state.selectedComponent === 'end' &&
      isPopopverOpen &&
      !rightComponentReference?.current?.classList?.contains(
        selectedInputBorder || 'fr-selected-input'
      )
    ) {
      rightComponentReference?.current?.classList.add(
        ''.concat(selectedInputBorder || 'fr-selected-input')
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPopopverOpen, state.selectedComponent, state.valid, isValidEnd]);

  if (isMobile && isMobileOpen) {
    setTimeout(() => {
      if (component === 'start') {
        leftComponentReference?.current?.classList.add(
          ''.concat(selectedInputBorder || 'fr-selected-input')
        );
        rightComponentReference?.current?.classList?.remove(
          selectedInputBorder || 'fr-selected-input'
        );
      } else {
        rightComponentReference?.current?.classList.add(
          ''.concat(selectedInputBorder || 'fr-selected-input')
        );
        leftComponentReference?.current?.classList?.remove(
          selectedInputBorder || 'fr-selected-input'
        );
      }
    }, 50);
  }
  if (isMobile && !isMobileOpen) {
    setTimeout(() => {
      rightComponentReference?.current?.classList?.remove(
        selectedInputBorder || 'fr-selected-input'
      );

      leftComponentReference?.current?.classList?.remove(
        selectedInputBorder || 'fr-selected-input'
      );
    }, 50);
  }

  const departureRef = React.useRef<any>(null);
  const returnRef = React.useRef<any>(null);

  return (
    <Box display="block" shadow="none" {...inputItem?.container} dataTestId="dateRangeSelector">
      <Box className="input-items-container" display="flex" flex="2" shadow="none">
        <Box display="block" shadow="none" width="100" {...inputItem?.leftContainer}>
          {showLabel && (
            <Text
              display="inline-block"
              textAlign="left"
              width="100"
              id={`input-label-date-range-${componentIdLeft}`}
              htmlFor={`input-fr-depart-input${componentIdLeft}`}
              {...textLabel}
            >
              {translate(departureLabelKey, 'Departure')}
            </Text>
          )}
          <Box
            border
            display="flex"
            className={`${className} ${classNameCmpLeft}`}
            style={{ overflow: 'hidden', ...style }}
            dataTestId="dateRangeSelectorDeparture"
            bgColor="white"
            focusReferences={(e) => {
              departureRef.current = e;
            }}
          >
            <Box
              className={`fr-input fr-input-group fr-w-100 ${currentOnValueClass}`}
              focusReferences={leftComponentReference}
            >
              <VisuallyHidden aria-live="polite">
                {!isValid
                  ? translate(
                      `cmp-label-datepicker-departure-error-date`,
                      `Error on the departure date`
                    )
                  : ''}
              </VisuallyHidden>
              <Box flex="1" position="relative">
                <Input
                  rightIconDataTestId="buttonRightIconsDeparture"
                  buttonRightIconId={buttonDepartureIconId}
                  dataTestId="DepartureInput"
                  id={`fr-depart-input${componentIdLeft}`}
                  focusReferences={leftInputRef}
                  value={value?.start}
                  border={false}
                  paddingRight="1"
                  autoComplete={'off'}
                  spellCheck={'off'}
                  autoCorrect={'off'}
                  ariaAutoComplete={'list'}
                  className="fr-flex-2"
                  defaultValue={value?.start}
                  placeholder={translate(departInputPlaceholderLabelKey, 'Depart')}
                  onFocus={(event: $TSFixMe) => {
                    handleOnFocus(event, 'start');
                    dispatch({ type: 'SET_SELECTED_INPUT', payload: 'start' });
                  }}
                  onChange={(event: $TSFixMe) => {
                    event.persist();
                    handleOnChange(event, 'start');
                  }}
                  onClick={(event: $TSFixMe) => {
                    openPopupOnClick && onClick(event, 'start');
                    dispatch({ type: 'SET_SELECTED_INPUT', payload: 'start' });
                  }}
                  onKeyDown={(event: $TSFixMe) => {
                    event.persist();
                    handleKeyDown(event, 'start');
                  }}
                  onRightIconBlur={() => {
                    if (departureRef && departureRef?.current) {
                      departureRef?.current?.classList.remove('fr-input-invalid');
                    }
                    if (state.typeError !== VALID_DATE) {
                      dispatch({ type: 'RESET_ACTION_AND_CLEAR_STATE' });
                    }
                  }}
                  onBlur={(event: $TSFixMe) => {
                    handleOnBlur(event, 'start');
                    if (leftComponentReference && leftComponentReference.current) {
                      leftComponentReference?.current?.classList.remove(
                        focusClass || 'fr-focus:focus'
                      );
                    }
                    const relatedTargetId = (event?.relatedTarget as Element)?.getAttribute('id');
                    if (
                      relatedTargetId !== buttonDepartureIconId &&
                      relatedTargetId !== buttonDepartureClearId
                    ) {
                      if (departureRef && departureRef?.current) {
                        departureRef?.current?.classList.remove('fr-input-invalid');
                      }
                      if (state.typeError !== VALID_DATE) {
                        dispatch({ type: 'RESET_ACTION_AND_CLEAR_STATE' });
                      }
                    }
                  }}
                  leftIcons={['calendar']}
                  leftTabIndex={[-1]}
                  size={10}
                  rightAriaLabel={
                    isPopopverOpen
                      ? [translate('cmp-selector-aria-label-close-popover', 'Close popover')]
                      : [translate('cmp-selector-aria-label-open-popover', 'Open popover')]
                  }
                  onRightIconClick={(e: any) => {
                    if (isValid && leftComponentReference && leftComponentReference.current) {
                      leftComponentReference?.current?.classList.add(
                        ''.concat(selectedInputBorder || 'fr-selected-input')
                      );
                    }
                    handleRightButtonAction({ event: e });
                  }}
                  label={translate(departureLabelKey, 'Departure')}
                  ariaDescribedby={`fr-depart-label-${componentIdLeft}`}
                  ariaLabelledBy={showLabel ? `input-label-date-range-${componentIdLeft}` : null}
                  ariaLabelDescription={`${translate(
                    'cmp-date-range-selector-instructions',
                    'Type the date in this format'
                  )} ${translate(
                    `date-format-${removeInvalidCharacters(replaceAll(format, ' ', '-'))}`,
                    format
                  )}`}
                  showFocusVisualCue={false}
                  {...inputItem}
                  {...leftInput}
                />
              </Box>
              {showCloseDepartureIcon ? (
                <Box className="fr-input-group-append">
                  <Button
                    color="transparent"
                    ariaLabel={translate(
                      `cmp-date-range-selector-${
                        showCloseDepartureIcon ? 'clear-filter' : 'right-icon'
                      }`,
                      'Clear'
                    )}
                    paddingWidth="2"
                    dataTestId="ClearButton"
                    {...clearButton}
                    onClick={(event) => {
                      clearAction({ event, showCloseDepartureIcon });
                    }}
                    onKeyDown={() => {
                      // if (!this.isFocused && this.popover && !event.shiftKey && event.keyCode === 9)
                      //   this.popover.closePopover();
                    }}
                    tabIndex={showCloseDepartureIcon ? null : -1}
                    onBlur={(event) => {
                      if (departureRef && departureRef?.current) {
                        departureRef?.current?.classList.remove('fr-input-invalid');
                      }
                      const relatedTargetId = (event?.relatedTarget as Element)?.getAttribute('id');
                      if (
                        relatedTargetId !== buttonDepartureIconId &&
                        relatedTargetId !== buttonDepartureClearId
                      ) {
                        if (departureRef && departureRef?.current) {
                          departureRef?.current?.classList.remove('fr-input-invalid');
                        }
                        if (state.typeError !== VALID_DATE) {
                          dispatch({ type: 'RESET_ACTION_AND_CLEAR_STATE' });
                        }
                      }
                    }}
                    id={buttonDepartureClearId}
                  >
                    <Icon
                      align="middle"
                      icon={
                        showCloseDepartureIcon
                          ? iconClose
                          : isPopopverOpen
                          ? rightIconOpen.icon
                          : rightIcon
                      }
                      {...rightIconSettings}
                    />
                  </Button>
                </Box>
              ) : null}
            </Box>
          </Box>
          <Box color={'red'} {...errorLabelComponent}>
            {(state.inputValue.start === '' || state.inputValue.start === undefined) &&
              isRequired &&
              translate(`cmp-label-datepicker-departure-error-empty-input`, `Input departure date`)}
            {!state.valid &&
              state.typeError === INCORRECT_FORMAT &&
              state.inputValue?.start !== '' &&
              translate(
                `cmp-label-datepicker-departure-error-incorrect-format-date`,
                `Wrong format on the departure date.`
              )}
            {!state.valid &&
              state.typeError === DATE_OUT_OF_RANGE &&
              state.inputValue?.start !== '' &&
              translate(
                `cmp-label-datepicker-departure-error-date-out-of-range`,
                `Departure date out of range.`
              )}
          </Box>
          {childrenLeft}

          {/* <Box textColor="danger">
          {!isValid
            ? translate(`cmp-label-datepicker-${label}-error-date`, `Error on the ${label} date`)
            : ''}
        </Box> */}
        </Box>

        <Box
          display="block"
          shadow="none"
          paddingLeft="2"
          width="100"
          {...inputItem?.rightContainer}
        >
          {showLabel && (
            <Text
              display="inline-block"
              textAlign="left"
              width="100"
              id={`input-label-date-range-${componentIdRight}`}
              htmlFor={`input-fr-return-input${componentIdRight}`}
              {...textLabel}
            >
              {translate(returnLabelKey, 'Return')}
            </Text>
          )}
          <Box
            border
            display="flex"
            className={`${className} ${classNameCmpRight}`}
            style={{ overflow: 'hidden', ...style }}
            dataTestId="dateRangeSelectorReturn"
            bgColor="white"
            focusReferences={(e) => {
              returnRef.current = e;
            }}
          >
            <Box
              className={`fr-input fr-input-group fr-w-100 ${currentOnValueClass}`}
              focusReferences={rightComponentReference}
            >
              <VisuallyHidden aria-live="polite">
                {!isValid
                  ? translate(`cmp-label-datepicker-return-error-date`, `Error on the return date`)
                  : ''}
              </VisuallyHidden>
              <Box flex="1" position="relative">
                <Input
                  buttonRightIconId={buttonReturnIconId}
                  rightIconDataTestId="buttonRightIconsReturn"
                  dataTestId="ReturnInput"
                  id={`fr-return-input${componentIdRight}`}
                  focusReferences={rightInputRef}
                  value={value?.end}
                  border={false}
                  paddingRight="1"
                  autoComplete={'off'}
                  spellCheck={'off'}
                  autoCorrect={'off'}
                  ariaAutoComplete={'list'}
                  className="fr-flex-2"
                  defaultValue={value?.end}
                  placeholder={translate(returnInputPlaceholderLabelKey, 'Return')}
                  onFocus={(event: $TSFixMe) => {
                    handleOnFocus(event, 'end');
                    dispatch({ type: 'SET_SELECTED_INPUT', payload: 'end' });
                  }}
                  onChange={(event: $TSFixMe) => {
                    event.persist();
                    handleOnChange(event, 'end');
                  }}
                  onClick={(event: $TSFixMe) => {
                    openPopupOnClick && onClick(event, 'end');
                    dispatch({ type: 'SET_SELECTED_INPUT', payload: 'end' });
                  }}
                  onKeyDown={(event: $TSFixMe) => {
                    event.persist();
                    handleKeyDown(event, 'end');
                  }}
                  onRightIconBlur={() => {
                    if (returnRef && returnRef?.current) {
                      returnRef?.current?.classList.remove('fr-input-invalid');
                    }
                    if (state.typeError !== VALID_DATE) {
                      dispatch({ type: 'RESET_ACTION_AND_CLEAR_STATE' });
                    }
                  }}
                  onBlur={(event: $TSFixMe) => {
                    handleOnBlur(event, 'end');
                    if (rightComponentReference && rightComponentReference.current) {
                      rightComponentReference?.current?.classList.remove(
                        focusClass || 'fr-focus:focus'
                      );
                    }
                    const relatedTargetId = (event?.relatedTarget as Element)?.getAttribute('id');
                    if (
                      relatedTargetId !== buttonReturnIconId &&
                      relatedTargetId !== buttonReturnClearId
                    ) {
                      if (returnRef && returnRef?.current) {
                        returnRef?.current?.classList.remove('fr-input-invalid');
                      }
                      if (state.typeError !== VALID_DATE) {
                        dispatch({ type: 'RESET_ACTION_AND_CLEAR_STATE' });
                      }
                    }
                  }}
                  leftIcons={['calendar']}
                  leftTabIndex={[-1]}
                  // rightTabIndex={[-1]}
                  size={10}
                  rightAriaLabel={
                    isPopopverOpen
                      ? [translate('cmp-selector-aria-label-close-popover', 'Close popover')]
                      : [translate('cmp-selector-aria-label-open-popover', 'Open popover')]
                  }
                  onRightIconClick={(e: any) => {
                    if (isValidEnd && rightComponentReference && rightComponentReference.current) {
                      rightComponentReference?.current?.classList.add(
                        ''.concat(selectedInputBorder || 'fr-selected-input')
                      );
                    }
                    handleRightButtonAction({ event: e });
                  }}
                  label={translate(returnLabelKey, 'Return')}
                  ariaDescribedby={`fr-return-label-${componentIdRight}`}
                  ariaLabelledBy={showLabel ? `input-label-date-range-${componentIdRight}` : null}
                  ariaLabelDescription={`${translate(
                    'cmp-date-range-selector-instructions',
                    'Type the date in this format'
                  )} ${translate(
                    `date-format-${removeInvalidCharacters(replaceAll(format, ' ', '-'))}`,
                    format
                  )}`}
                  showFocusVisualCue={false}
                  {...inputItem}
                  {...rightInput}
                />
              </Box>
              {showCloseReturnIcon ? (
                <Box className="fr-input-group-append">
                  <Button
                    color="transparent"
                    ariaLabel={translate(
                      `cmp-date-range-selector-${
                        showCloseReturnIcon ? 'clear-filter' : 'right-icon'
                      }`,
                      'Clear'
                    )}
                    paddingWidth="2"
                    dataTestId="ClearButton"
                    {...clearButton}
                    onClick={(event) => {
                      clearAction({ event, showCloseReturnIcon });
                    }}
                    onKeyDown={() => {
                      // if (!this.isFocused && this.popover && !event.shiftKey && event.keyCode === 9)
                      //   this.popover.closePopover();
                    }}
                    tabIndex={showCloseReturnIcon ? null : -1}
                    onBlur={(event) => {
                      if (returnRef && returnRef?.current) {
                        returnRef?.current?.classList.remove('fr-input-invalid');
                      }
                      const relatedTargetId = (event?.relatedTarget as Element)?.getAttribute('id');
                      if (
                        relatedTargetId !== buttonReturnIconId &&
                        relatedTargetId !== buttonReturnClearId
                      ) {
                        if (returnRef && returnRef?.current) {
                          returnRef?.current?.classList.remove('fr-input-invalid');
                        }
                        if (state.typeError !== VALID_DATE) {
                          dispatch({ type: 'RESET_ACTION_AND_CLEAR_STATE' });
                        }
                      }
                    }}
                    id={buttonReturnClearId}
                  >
                    <Icon
                      align="middle"
                      icon={
                        showCloseReturnIcon
                          ? iconClose
                          : isPopopverOpen
                          ? rightIconOpen.icon
                          : rightIcon
                      }
                      {...rightIconSettings}
                    />
                  </Button>
                </Box>
              ) : null}
            </Box>
          </Box>
          <Box color={'red'} {...errorLabelComponent}>
            {(state.inputValue.end === '' || state.inputValue.end === undefined) &&
              isRequired &&
              translate(`cmp-label-datepicker-return-error-empty-input`, `Input return date`)}
            {!state.validEnd &&
              state.typeErrorEnd === INCORRECT_FORMAT &&
              state.inputValue?.end !== '' &&
              translate(
                `cmp-label-datepicker-return-error-incorrect-format-date`,
                `Wrong format on the return date.`
              )}
            {!state.validEnd &&
              state.typeErrorEnd === DATE_OUT_OF_RANGE &&
              state.inputValue?.end !== '' &&
              translate(
                `cmp-label-datepicker-return-error-date-out-of-range`,
                `Return date out of range.`
              )}
          </Box>
          {childrenRight}
          {/* <Box textColor="danger">
          {!isValid
            ? translate(`cmp-label-datepicker-${label}-error-date`, `Error on the ${label} date`)
            : ''}
        </Box> */}
        </Box>
      </Box>
    </Box>
  );
};

export default InputDateRangePicker;
